import React, { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { Link } from 'react-router-dom';

import Container from 'components/container';
import Button from 'components/button';

import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { fetch } from 'services/fetch';
import { convert } from 'routes/uris';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

interface ParisFile {
    id: number
    filename: string
    name: string
    original_filename: string
    status: string
    created_at: Date
}

const Home: React.FC = () => {
    const [uploadProgress, setUploadProgress] = useState<number | string>(0);

    const [loading, setLoading] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);

    const [filterBy, setFilterBy] = useState<string>('');

    const [files, setFiles] = useState<ParisFile[]>([]);
    const [filesList, setFilesList] = useState<ParisFile[]>([]);

    const [name, setName] = useState<string>('');
    const [file, setFile] = useState<File | undefined>(undefined);

    const [fileInvalid, setFileInvalid] = useState<boolean>(false);

    useEffect(() => {
        fetchFiles();
    }, []);

    const fetchFiles = () => {
        fetch('/files', 'GET', {}, true)
            .then((result: AxiosResponse) => {
                setFilesList(result.data.data);
                setFiles(result.data.data);
            });
    };

    const handleSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = target;
        // set filter by string that user typed:
        setFilterBy(value);

        let filteredList: ParisFile[] = [...filesList];

        if (value !== '') {
            const filterQuery = value.toLowerCase();

            filteredList = filteredList
                .filter((item) => {
                    const foundInName: boolean = item.name
                        .toLowerCase()
                        .indexOf(filterQuery) >= 0;
                    const foundInFilename: boolean = item.original_filename
                        .toLowerCase()
                        .indexOf(filterQuery) >= 0;

                    return foundInFilename || foundInName;
                });
        }

        setFiles(filteredList);
    };

    const handleFileSelection = (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.files.length) {
            setFile(ev.target.files[0]);
        }

        setFileInvalid(false);
    };

    const upload = async (event: React.MouseEvent<HTMLFormElement>) => {
        const formElement = event.currentTarget;

        // prevent submit action:
        event.preventDefault();

        if (!formElement.checkValidity()) {
            event.stopPropagation();
        }

        setValidated(true);

        if (formElement.reportValidity()) {
            setLoading(true);
            setFileInvalid(false);

            const formData: FormData = new FormData();

            formData.append('name', name);
            formData.append('file', file);

            fetch(
                '/files',
                'POST',
                formData,
                true,
                {
                    onUploadProgress: (progressEvent: { loaded: number; total: number; }) => {
                        const currentProgress = ((progressEvent.loaded / progressEvent.total) * 100).toFixed(2);
                        // set upload progress value:
                        setUploadProgress(currentProgress);
                    },
                }
            )
                .then(() => {
                    return fetchFiles();
                })
                .catch(() => {
                    setFileInvalid(true);
                })
                .finally(() => {
                    // on success, empty form and component's state
                    // to be able to execute another upload. After that,
                    // fetch all the files to get the last inserted file:
                    formElement.reset();
                    
                    setName('');
                    setFile(undefined);

                    setLoading(false);
                    setValidated(false);
                });

        }
    };

    return (
        <Container>
            <h2 className="mt-3 mb-0">Upload an MS SQL Database File</h2>
            <p className="mb-4">Please read the <a href="https://flash-team.atlassian.net/l/c/zpCP3qJT">how to</a> instructions on using the conversion tool.<br/>To upload an access file, please first convert it to MS SQL format </p>
            <Form noValidate validated={validated} id="file-upload-form" onSubmit={upload}>
                <Form.Group className="mb-2" controlId="formImportName">
                    <Form.Label>Name of import</Form.Label>
                    <Form.Control
                        type="text"
                        name="name"
                        aria-label="Name of import"
                        onChange={({ target }) => setName(target.value)}
                        value={name}
                        required
                    />

                    <Form.Control.Feedback type="invalid">
                        This field is required
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Control
                    type="file"
                    accept=".sql"
                    className="form-control"
                    name="file"
                    onChange={handleFileSelection}
                    required
                />

                {fileInvalid === true && (
                    <Form.Text className="text-danger d-block">
                        Please choose a file that is valid
                    </Form.Text>
                )}

                <Form.Text>
                    Only files of <em>.sql</em> extension allowed
                </Form.Text>

                <div className="d-flex align-items-center flex-row mb-1 mt-2">
                    <Button
                        type="submit"
                        className="btn btn-primary me-2"
                        loading={loading}
                    >
                        Upload File
                    </Button>
                </div>


                {uploadProgress > 0 && loading && (
                    <p>{`Uploading: ${uploadProgress}%`}</p>
                )}
            </Form>

            <Row className="row align-items-center mb-4">
                <Col xs={8} lg={9}>
                    <h3 className="mt-3">Loaded and In Progress Files</h3>
                </Col>

                <Col xs={4} lg={3}>
                    <InputGroup>
                        <InputGroup.Text id="search-by-name">
                            <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>

                        <Form.Control
                            aria-describedby="search-by-name"
                            aria-label="Filter by name or file name"
                            placeholder="Filter by name or file name"
                            onChange={handleSearchChange}
                            value={filterBy}
                        />
                    </InputGroup>
                </Col>
            </Row>


            <Table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>File Name</th>
                        <th>Progress</th>
                        <th>Conversion</th>
                    </tr>
                </thead>
                <tbody>
                    {files.map((file: ParisFile) => {
                        return (
                            <tr key={`file-${file.id}`}>
                                <td>{file.name}</td>
                                <td>{file.original_filename}</td>
                                {/* @todo: get status formatted through importing */}
                                <td>{file.status}</td>
                                <td>{(['done', 'conversion done', 'conversion error'].indexOf(file.status) >= 0) ? <Link to={convert.replace(':id', file.id.toString())}>Convert</Link> : ''}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </Container>
    );
};

export default Home;
